/**
 * Lógica pura de manufactura (CC-6: NUNCA usar macros lingui aquí).
 *
 * Una lista de materiales (bill_of_materials) define los componentes
 * (bom_component: producto × cantidad) necesarios para producir un LOTE de
 * receta del producto terminado (bom.qty unidades).
 *
 * Una orden de fabricación (manufacturing_order) produce `qty` unidades del
 * terminado. Para ello:
 *   1) ESCALA cada componente: needed = component.qty × (orderQty / bom.qty).
 *   2) CONSUME los componentes (stock_move de salida + capa AVCO negativa al
 *      costo promedio vigente de cada componente).
 *   3) PRODUCE el terminado (stock_move de entrada + capa AVCO positiva) cuyo
 *      COSTO UNITARIO se deriva de Σ(costo de componentes consumidos) / orderQty.
 *
 * Cantidades en ENTEROS (unidades menores, factor 1000), igual que F1–F4.
 * Los COSTOS UNITARIOS son por unidad entera (no por milésima).
 */

import { QTY_SCALE } from "./units";
import { type AvcoState, averageUnitCost, valueOf } from "./valuation";

export type ManufacturingState = "draft" | "done";

export const MANUFACTURING_STATES: readonly ManufacturingState[] = ["draft", "done"] as const;

export function isManufacturingState(value: string): value is ManufacturingState {
  return (MANUFACTURING_STATES as readonly string[]).includes(value);
}

export function coerceManufacturingState(value: string | null | undefined): ManufacturingState {
  return value && isManufacturingState(value) ? value : "draft";
}

/** ¿Se puede fabricar (borrador→hecho)? Sólo desde borrador. */
export function canProduce(state: string | null | undefined): boolean {
  return coerceManufacturingState(state) === "draft";
}

/** Componente de una BoM (subset relevante para el cálculo). */
export interface BomComponentLike {
  product_id: string | null;
  /** Cantidad de componente por lote de receta (unidades menores). */
  qty: number | null;
}

/**
 * Escala la cantidad de un componente a la cantidad de la orden.
 *   needed = componentQty × orderQty / bomQty
 * `bomQty` es la cantidad de terminado que produce un lote de receta (>0).
 * Todas las cantidades están escaladas ×1000; el factor se cancela en la razón
 * orderQty/bomQty, así que se multiplica y luego se divide por bomQty.
 */
export function scaleComponentQty(componentQty: number, orderQty: number, bomQty: number): number {
  if (bomQty <= 0) {
    return 0;
  }
  return Math.round((componentQty * orderQty) / bomQty);
}

/** Consumo planificado de un componente para una orden de fabricación. */
export interface ComponentConsumption {
  productId: string;
  /** Cantidad a consumir (unidades menores, > 0). */
  qty: number;
}

/**
 * Planifica el consumo de componentes de una orden: escala cada componente de la
 * BoM a la cantidad a fabricar. Ignora componentes sin producto o con cantidad
 * <= 0. Determinista, sin efectos.
 */
export function planComponentConsumption(
  components: BomComponentLike[],
  orderQty: number,
  bomQty: number,
): ComponentConsumption[] {
  const plan: ComponentConsumption[] = [];
  for (const c of components) {
    const productId = c.product_id;
    if (!productId) {
      continue;
    }
    const qty = scaleComponentQty(c.qty ?? 0, orderQty, bomQty);
    if (qty <= 0) {
      continue;
    }
    plan.push({ productId, qty });
  }
  return plan;
}

/** Costo de salida de un componente al consumirlo (AVCO vigente). */
export interface ConsumedComponentCost {
  productId: string;
  /** Cantidad consumida (unidades menores). */
  qty: number;
  /** Costo promedio móvil vigente por unidad entera. */
  unitCost: number;
  /** Valor consumido (unidades menores de dinero). */
  value: number;
}

/**
 * Valora el consumo de un componente al costo promedio móvil VIGENTE de su
 * estado AVCO. Devuelve el costo unitario y el valor (positivo) consumido.
 */
export function valueComponentConsumption(
  state: AvcoState,
  qty: number,
): ConsumedComponentCost & { productId: string } {
  const unitCost = averageUnitCost(state);
  return {
    productId: "",
    qty,
    unitCost,
    value: valueOf(qty, unitCost),
  };
}

/**
 * Costo unitario del producto fabricado: suma del VALOR de todos los componentes
 * consumidos dividido entre la cantidad producida (por unidad entera).
 *
 *   producedUnitCost = (Σ value_componente × QTY_SCALE) / producedQty
 *
 * `consumedValue` está en unidades menores de dinero; `producedQty` está
 * escalado ×1000. Se multiplica por QTY_SCALE para obtener un costo POR UNIDAD
 * ENTERA (no por milésima). Devuelve 0 si producedQty <= 0.
 */
export function producedUnitCost(consumedValue: number, producedQty: number): number {
  if (producedQty <= 0) {
    return 0;
  }
  return Math.round((consumedValue * QTY_SCALE) / producedQty);
}
